import {
  RefObject,
  useState,
  ReactNode,
  KeyboardEventHandler,
  forwardRef,
} from 'react';
import styled, { css } from 'styled-components';
import clx from 'classnames';

import { RfqId } from '@paradigm/features/src/unified-rfqs/entities/rfq';

import arrowDown from '@paradigm/design-system/src/assets/rfq-row-arrow-down.svg';
import {
  blackTangaroa,
  firefly,
  linkWater,
  paradigmLightBlue,
  pickledBluewood,
  tangaroa,
} from '@paradigm/design-system/src/colors';

import createSafeContext from '@paradigm/utils/src/createSafeContext';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import GridCellBase, {
  GridCellProps,
} from '#/components/UnifiedRfqs/components/GridCellBase';
import VirtualRow from '#/components/rfq-common/VirtualRow';
import { useDebouncedTrackAction } from '@paradigm/features/src/analytics';

interface RowProps {
  readonly id: RfqId;
  readonly scrollableRef: RefObject<HTMLDivElement>;
  readonly rowContent: () => JSX.Element;
}

export default function Row({ id, scrollableRef, rowContent }: RowProps) {
  return (
    <Wrapper data-testid={id} aria-label={`rfq row ${id}`}>
      <VirtualRow scrollableRef={scrollableRef} component={rowContent} />
    </Wrapper>
  );
}

interface ExpandableRowProps {
  readonly children: ReactNode;
  readonly expandedContent: () => JSX.Element;
  readonly className?: string;
}

const [useIsRowExpanded, RowExpandedProvider] = createSafeContext<boolean>(
  'RowExpandedProvider',
);
export { useIsRowExpanded };

export const ExpandableContent = forwardRef<HTMLDivElement, ExpandableRowProps>(
  function ExpandableContent(
    {
      children,
      className,
      expandedContent: ExpandedContent,
    }: ExpandableRowProps,
    _ref,
  ) {
    const [isExpanded, setExpanded] = useState(false);
    const track = useDebouncedTrackAction();

    const handleExpand = () =>
      setExpanded((wasExpanded) => {
        const nextExpandedState = !wasExpanded;
        track(nextExpandedState ? 'expand market row' : 'collapse market row');
        return nextExpandedState;
      });
    const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      handleExpand();
    };

    return (
      <RowExpandedProvider value={isExpanded}>
        <Container
          className={clx(className, { 'row-expanded': isExpanded })}
          isExpanded={isExpanded}
        >
          <ClickableRowWrapper
            aria-expanded={isExpanded}
            onClick={handleExpand}
            onKeyDown={handleKeyDown}
          >
            {children}
          </ClickableRowWrapper>
          {isExpanded && <ExpandedContent />}
        </Container>
      </RowExpandedProvider>
    );
  },
);

const ClickableRowWrapper = styled.div.attrs({
  role: 'button',
  tabIndex: 0,
})`
  transition: background-color 0.14s ease;
  :focus {
    outline: none;
  }
`;

const Wrapper = styled.div`
  background: ${firefly};
  @media (min-width: ${MOBILE_BREAKPOINT}px) {
    :nth-child(odd) {
      background: #041c32;
    }
  }
`;

export const BaseRow = styled.div`
  display: grid;
  gap: 19px;
  align-items: center;
  padding: 0 8px;
`;

export const ROW_HEIGHT = 48;
export const ROW_HEIGHT_MOBILE = 196;

export const TableRow = styled(BaseRow)`
  color: #9ca1a7;
  font-size: 14px;
  line-height: 1rem;
  font-weight: 400;
  height: ${ROW_HEIGHT}px;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    height: ${ROW_HEIGHT_MOBILE}px;
  }
`;

// Promote element to a composite layer with translateZ
// translateZ triggers repaint when a row is added to avoid disappearing border
export const ActionsContainer = styled.div`
  display: grid;
  position: relative;
  height: ${ROW_HEIGHT}px;
  text-align: center;
  align-items: center;
  justify-content: center;
  transform: translateZ(0);
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    height: 100%;
  }
`;

export const TableHeader = styled(BaseRow)`
  position: sticky;
  top: 0;
  z-index: 10; /* reserve 1-9 range to row's content */
  background: ${tangaroa};
  color: ${paradigmLightBlue};
  font-size: 14px;
  line-height: 17px;
  font-weight: 400;
  height: 41px;
`;

const Container = styled.div.withConfig<{ isExpanded: boolean }>({
  shouldForwardProp: (prop) => prop !== 'isExpanded',
})`
  --row-arrow-display: ${({ isExpanded }) => (isExpanded ? 'none' : 'block')};
  border-bottom: 1px solid ${pickledBluewood};
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    --row-arrow-display: none;
  }
`;

const ARROW_DOWN_WIDTH = 10;
const ARROW_DOWN_HEIGHT = 4;

export const ArrowDown = ({ actionWidth }: { actionWidth: number }) => {
  const arrowDownLeft = (actionWidth - ARROW_DOWN_WIDTH) / 2;
  return (
    <img
      css={`
        position: absolute;
        left: ${arrowDownLeft}px;
        bottom: -${ARROW_DOWN_HEIGHT}px;
        margin: 0 auto;
        background: ${blackTangaroa};
        z-index: 1;
        display: var(--row-arrow-display, none);
      `}
      width={ARROW_DOWN_WIDTH}
      height={ARROW_DOWN_HEIGHT}
      src={arrowDown}
      alt=""
    />
  );
};

export const StrategyDescriptionCell = styled.div`
  color: ${linkWater};
  font-weight: 500;

  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

export const IdCell = styled.span`
  grid-column: 1 / span 2;
  align-self: start;
  margin-top: 8px;
  font-size: 12px;
  color: #b8bdc3;
`;

export const dividerCss = css`
  position: relative;

  &:before {
    content: '';
    display: block;
    position: absolute;
    background: linear-gradient(
      90deg,
      rgba(54, 121, 221, 0) 0%,
      rgba(54, 121, 221, 1) 50%,
      rgba(54, 121, 221, 0) 100%
    );
    opacity: 0.5;
    width: 100%;
    height: 1px;
    left: 0;
    right: 0;
    top: 0px;
  }
`;

export const MenuContainer = styled(GridCellBase).attrs<GridCellProps>(() => ({
  mobile: true,
  area: 'menu',
}))<GridCellProps>`
  opacity: 1;
  padding-left: 1rem;
  height: 100%;
  display: flex;
  align-items: center;
`;

export const Cell = styled(GridCellBase)`
  height: ${ROW_HEIGHT}px;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  justify-content: center;
  align-items: center;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    height: 100%;
    width: 100%;
  }
`;
